import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, Mail, TrendingUp, Settings, Menu as MenuIcon, Home, ExternalLink, LogOut, UtensilsCrossed, FileText, House, Calendar, Globe, ChefHat, CreditCard, Search } from 'lucide-react';
import { usePublicSubscriptionStatus } from '@/hooks/usePublicSubscriptionStatus';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarTrigger,
  SidebarHeader,
  useSidebar,
} from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';

type MenuItem = {
  url: string;
  icon: any;
  end: boolean;
} & ({ titleKey: string } | { title: string });

const mainMenuItems: MenuItem[] = [
  { titleKey: 'dashboardOverviewLink', url: '/dashboard', icon: LayoutDashboard, end: true },
  { titleKey: 'homePage', url: '/dashboard/home', icon: House, end: false },
  { titleKey: 'messages', url: '/dashboard/messages', icon: Mail, end: false },
  { titleKey: 'dashboardAnalytics', url: '/dashboard/analytics', icon: TrendingUp, end: false },
  { titleKey: 'menu', url: '/dashboard/menu', icon: UtensilsCrossed, end: false },
  { titleKey: 'blog', url: '/dashboard/blog', icon: FileText, end: false },
];

const premiumMenuItems: MenuItem[] = [
  { titleKey: 'dashboardContacts', url: '/dashboard/contacts', icon: Mail, end: false },
  { titleKey: 'catering', url: '/dashboard/catering', icon: ChefHat, end: false },
  { titleKey: 'reservations', url: '/dashboard/reservations', icon: Calendar, end: false },
  { title: 'Subscription Status', url: '/dashboard/subscription', icon: CreditCard, end: true },
];

interface DashboardSidebarProps {
  onLogout: () => void;
}

export function DashboardSidebar({ onLogout }: DashboardSidebarProps) {
  const { state, toggleSidebar } = useSidebar();
  const location = useLocation();
  const { language, toggleLanguage, t } = useLanguage();
  const { hasContactsAccess, hasReservationsAccess, hasCateringAccess } = usePublicSubscriptionStatus();
  const collapsed = state === 'collapsed';

  // Filter premium items based on subscription tier
  const visiblePremiumItems = premiumMenuItems.filter(item => {
    if (item.url === '/dashboard/contacts') return hasContactsAccess;
    if (item.url === '/dashboard/reservations') return hasReservationsAccess;
    if (item.url === '/dashboard/catering') return hasCateringAccess;
    // Subscription pages are always visible
    return true;
  });

  const isActive = (path: string, end: boolean) => {
    if (end) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <Sidebar
      className={`${collapsed ? 'w-14' : 'w-60'} bg-white dark:bg-background`}
      collapsible="icon"
    >
      <SidebarHeader>
        <div className="flex items-center justify-between p-2">
          <SidebarTrigger className="shrink-0" />
          {!collapsed && <h1 className="text-lg font-semibold ml-2">Management Portal</h1>}
        </div>
      </SidebarHeader>
      
      <SidebarContent>
        {/* Main Menu */}
        <SidebarGroup>
          <SidebarGroupLabel className={collapsed ? 'text-center text-xs' : 'text-xs'}>
            {collapsed ? <MenuIcon className="h-4 w-4" /> : t('management')}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainMenuItems.map((item) => {
                const active = isActive(item.url, item.end);
                const displayText = 'titleKey' in item ? t(item.titleKey) : item.title;
                const key = 'titleKey' in item ? item.titleKey : item.title;
                return (
                  <SidebarMenuItem key={key}>
                    <SidebarMenuButton asChild>
                      <NavLink 
                        to={item.url} 
                        end={item.end}
                        className={active ? 'bg-muted' : 'hover:bg-muted/50'}
                      >
                        <item.icon className="h-4 w-4" />
                        {!collapsed && <span className="text-sm">{displayText}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <Separator className="my-2" />

        {/* Premium Features */}
        {visiblePremiumItems.length > 0 && (
          <>
            <SidebarGroup>
              <SidebarGroupLabel className={collapsed ? 'text-center text-xs' : 'text-xs'}>
                {collapsed ? '⭐' : 'Premium Features'}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {visiblePremiumItems.map((item) => {
                    const active = isActive(item.url, item.end);
                    const displayText = 'titleKey' in item ? t(item.titleKey) : item.title;
                    const key = 'titleKey' in item ? item.titleKey : item.title;
                    return (
                      <SidebarMenuItem key={key}>
                        <SidebarMenuButton asChild>
                          <NavLink 
                            to={item.url} 
                            end={item.end}
                            className={active ? 'bg-muted' : 'hover:bg-muted/50'}
                          >
                            <item.icon className="h-4 w-4" />
                            {!collapsed && <span className="text-sm">{displayText}</span>}
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <Separator className="my-2" />
          </>
        )}

      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          {/* Settings */}
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <NavLink 
                to="/dashboard/settings" 
                className={isActive('/dashboard/settings', false) ? 'bg-muted' : 'hover:bg-muted/50'}
              >
                <Settings className="h-4 w-4" />
                {!collapsed && <span className="text-sm">{t('dashboardSettings')}</span>}
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <Separator className="my-1" />

          {/* Main Website */}
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <NavLink to="/" className="hover:bg-muted/50">
                <Home className="h-4 w-4" />
                {!collapsed && <span className="text-sm">{t('mainWebsite')}</span>}
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <Separator className="my-1" />

          {/* Language Toggle */}
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-start hover:bg-muted/50"
                onClick={toggleLanguage}
              >
                <Globe className="h-4 w-4" />
                {!collapsed && <span className="text-sm">{language === 'en' ? 'Traducir a Español' : 'Translate to English'}</span>}
              </Button>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <Separator className="my-1" />

          {/* Collapse Sidebar */}
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <button className="w-full flex items-center gap-2 px-2 py-2 hover:bg-muted/50 rounded-md" onClick={toggleSidebar}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="18" height="18" x="3" y="3" rx="2"/>
                  <path d="M9 3v18"/>
                </svg>
                {!collapsed && <span className="text-sm">Collapse</span>}
              </button>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <Separator className="my-1" />

          {/* Logout */}
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-start hover:bg-muted/50"
                onClick={onLogout}
              >
                <LogOut className="h-4 w-4" />
                {!collapsed && <span className="text-sm">Logout</span>}
              </Button>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
